/* NAMVEXO personalization — stable single-controller implementation */
(() => {
  'use strict';

  const KEY = '__nmvxPersonalizationStableV3__';
  if (window[KEY]?.boot) {
    window[KEY].boot();
    return;
  }

  const SELECTOR = '[data-nmvx-personalization]';
  const controllers = new WeakMap();
  const allowedLogoExtensions = new Set(['png', 'jpg', 'jpeg', 'svg', 'pdf']);
  const allowedLogoTypes = new Set(['image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf']);
  const maxLogoBytes = 10 * 1024 * 1024;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  let globalGuardsReady = false;
  let lifecycleReady = false;

  const normalize = (value = '') => String(value)
    .normalize('NFKD')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

  const optionNameMatches = (candidate, expected) => {
    const left = normalize(candidate);
    const right = normalize(expected);
    return Boolean(left && right && (left === right || left.includes(right)));
  };

  const isCustomAmmoLid = (value) => {
    const normalized = normalize(value);
    return normalized.includes('custom name logo')
      || (normalized.includes('custom') && normalized.includes('name') && normalized.includes('logo'));
  };

  const phoneDigits = (value) => String(value || '').replace(/\D/g, '');
  const isValidPhone = (value) => {
    const trimmed = String(value || '').trim();
    const digits = phoneDigits(trimmed);
    return /^[+\d\s().-]+$/.test(trimmed) && digits.length >= 7 && digits.length <= 15;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const logoError = (file) => {
    if (!file) return '';
    if (file.size > maxLogoBytes) return 'The logo file must be 10 MB or smaller.';

    const extension = file.name.includes('.') ? file.name.split('.').pop().toLocaleLowerCase() : '';
    const mime = (file.type || '').toLocaleLowerCase();
    const validMime = !mime || mime === 'application/octet-stream' || allowedLogoTypes.has(mime);

    return allowedLogoExtensions.has(extension) && validMime
      ? ''
      : 'Upload a PNG, JPG, JPEG, SVG, or PDF logo file.';
  };

  function activeCardForForm(form) {
    if (!(form instanceof HTMLFormElement)) return null;
    const formId = form.getAttribute('id');
    if (!formId) return null;

    return Array.from(document.querySelectorAll(SELECTOR))
      .find((card) => !card.hidden && card.dataset.formId === formId) || null;
  }

  function activeCardForControl(control) {
    const direct = activeCardForForm(control.closest('form'));
    if (direct) return direct;

    const section = control.closest('.shopify-section') || control.closest('product-information');
    if (section) {
      const card = Array.from(section.querySelectorAll(SELECTOR)).find((item) => !item.hidden);
      if (card) return card;
    }

    const visibleCards = Array.from(document.querySelectorAll(SELECTOR)).filter((item) => !item.hidden);
    return visibleCards.length === 1 ? visibleCards[0] : null;
  }

  function blockIncomplete(card, event) {
    const controller = controllers.get(card);
    if (!controller) return false;

    const state = controller.reconcile();
    if (!state.active || state.complete) {
      controller.prepareSubmission();
      return false;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    controller.showRequiredError();
    return true;
  }

  function installGlobalGuards() {
    if (globalGuardsReady) return;
    globalGuardsReady = true;

    document.addEventListener('click', (event) => {
      try {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const purchaseControl = target.closest(
          '[ref="addToCartButton"], button[name="add"], add-to-cart-component button, sticky-add-to-cart button'
        );
        if (!purchaseControl || purchaseControl.closest('[data-nmvx-remove-file]')) return;

        const card = activeCardForControl(purchaseControl);
        if (card) blockIncomplete(card, event);
      } catch (error) {
        console.error('[NAMVEXO personalization] Click validation failed safely.', error);
      }
    }, true);

    document.addEventListener('submit', (event) => {
      try {
        const card = activeCardForForm(event.target);
        if (!card) return;
        blockIncomplete(card, event);
      } catch (error) {
        console.error('[NAMVEXO personalization] Submit validation failed safely.', error);
      }
    }, true);
  }

  function selectedOptionValue(controller) {
    const expectedName = controller.card.dataset.optionName || 'Lid Design';
    const picker = controller.variantPicker;

    if (picker && typeof picker.getAllSelectedOptions === 'function') {
      try {
        const match = picker.getAllSelectedOptions()
          .find((option) => optionNameMatches(option?.name, expectedName));
        if (match) return match.value || '';
      } catch (error) {
        console.warn('[NAMVEXO personalization] Selected option lookup failed safely.', error);
      }
    }

    const root = picker || controller.context;
    for (const select of root.querySelectorAll('select')) {
      const option = select.selectedOptions?.[0];
      const name = option?.dataset?.optionName || select.dataset.optionName || select.name;
      if (optionNameMatches(name, expectedName)) return select.value || option?.value || '';
    }

    for (const input of root.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked')) {
      const name = input.dataset.optionName || input.name;
      if (optionNameMatches(name, expectedName)) return input.value || '';
    }

    return '';
  }

  function createReadiness(card) {
    const header = card.querySelector('.nmvx-personalization__header');
    if (!header) return null;

    let readiness = header.querySelector('[data-nmvx-readiness]');
    if (readiness) return readiness;

    readiness = document.createElement('span');
    readiness.className = 'nmvx-personalization__readiness';
    readiness.dataset.nmvxReadiness = '';
    readiness.setAttribute('aria-live', 'polite');
    header.appendChild(readiness);
    return readiness;
  }

  function createRemoveButton(card, fileInput, uploadLabel) {
    if (!(fileInput instanceof HTMLInputElement) || !uploadLabel) return null;

    let button = card.querySelector('[data-nmvx-remove-file]');
    if (button) return button;

    button = document.createElement('button');
    button.type = 'button';
    button.className = 'nmvx-personalization__remove-file';
    button.dataset.nmvxRemoveFile = '';
    button.hidden = true;
    button.innerHTML = '<span aria-hidden="true">×</span><span>Remove file</span>';
    uploadLabel.insertAdjacentElement('afterend', button);
    return button;
  }

  function initCard(card) {
    if (controllers.has(card)) {
      controllers.get(card).schedule(true);
      return;
    }

    const form = document.getElementById(card.dataset.formId || '');
    const primary = card.querySelector('[data-nmvx-primary]');
    if (!(form instanceof HTMLFormElement) || !(primary instanceof HTMLInputElement)) return;

    const mode = card.dataset.mode;
    const phone = card.querySelector('[data-nmvx-phone]');
    const fileInput = card.querySelector('[data-nmvx-file]');
    const context = card.closest('.product-details')
      || card.closest('product-information')
      || card.closest('.shopify-section')
      || document;
    const variantPicker = context.querySelector?.('variant-picker') || null;
    const count = card.querySelector('[data-nmvx-count]');
    const phoneCount = card.querySelector('[data-nmvx-phone-count]');
    const summary = card.querySelector('[data-nmvx-summary]');
    const error = card.querySelector('[data-nmvx-error]');
    const uploadLabel = card.querySelector('[data-nmvx-upload-label]');
    const uploadTitle = card.querySelector('[data-nmvx-upload-title]');
    const uploadMeta = card.querySelector('[data-nmvx-upload-meta]');
    const readiness = createReadiness(card);
    const removeFile = createRemoveButton(card, fileInput, uploadLabel);
    const abortController = new AbortController();
    const { signal } = abortController;

    form.enctype = 'multipart/form-data';

    const buyButtons = context.querySelector?.('.buy-buttons-block');
    if (buyButtons && card.nextElementSibling !== buyButtons) {
      buyButtons.insertAdjacentElement('beforebegin', card);
    }

    let attempted = false;
    let currentFileError = '';
    let frame = 0;
    let secondFrame = 0;
    let summarySignature = '';
    let temporarilyDisabled = [];

    const clearVisualError = () => {
      primary.removeAttribute('aria-invalid');
      phone?.removeAttribute('aria-invalid');
      uploadLabel?.removeAttribute('data-invalid');
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
    };

    const showVisualError = (message, target = primary) => {
      target?.setAttribute('aria-invalid', 'true');
      if (mode === 'ammo') uploadLabel?.setAttribute('data-invalid', 'true');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    };

    const syncActivation = () => {
      const active = mode !== 'ammo' || isCustomAmmoLid(selectedOptionValue(controller));

      if (card.hidden === active) card.hidden = !active;
      primary.disabled = !active;
      if (phone instanceof HTMLInputElement) phone.disabled = !active;
      if (fileInput instanceof HTMLInputElement) fileInput.disabled = !active;

      if (!active) {
        primary.value = '';
        if (phone instanceof HTMLInputElement) phone.value = '';
        if (fileInput instanceof HTMLInputElement) fileInput.value = '';
        attempted = false;
        currentFileError = '';
        card.removeAttribute('data-attempted');
      }

      return active;
    };

    const state = () => {
      const active = syncActivation();
      const primaryRaw = primary.value;
      const primaryFilled = Boolean(primaryRaw.trim());
      const phoneRaw = phone instanceof HTMLInputElement ? phone.value : '';
      const phoneFilled = Boolean(phoneRaw.trim());
      const phoneValid = mode !== 'tag' || (phoneFilled && isValidPhone(phoneRaw));
      const file = fileInput instanceof HTMLInputElement ? fileInput.files?.[0] || null : null;

      let complete = false;
      if (active) {
        if (mode === 'ammo') complete = Boolean(primaryFilled || file);
        if (mode === 'pet') complete = primaryFilled;
        if (mode === 'tag') complete = primaryFilled && phoneValid;
      }

      return { active, complete, primaryRaw, primaryFilled, phoneRaw, phoneFilled, phoneValid, file };
    };

    const updateSummary = (current) => {
      if (!summary) return;

      const entries = [];
      if (mode === 'pet') {
        if (current.primaryFilled) entries.push(['Pet name', current.primaryRaw]);
        else entries.push(['Required', 'Enter your pet’s name']);
      }
      if (mode === 'ammo') {
        if (current.primaryFilled) entries.push(['Custom text', current.primaryRaw]);
        if (current.file) entries.push(['Logo file', current.file.name]);
        if (!entries.length) entries.push(['Required', 'Add text, a logo, or both']);
      }
      if (mode === 'tag') {
        if (current.primaryFilled) entries.push(['Dog name', current.primaryRaw]);
        if (current.phoneFilled) entries.push(['Phone number', current.phoneRaw]);
        if (!entries.length) entries.push(['Required', 'Enter a dog name and phone number']);
      }

      const nextSignature = JSON.stringify(entries);
      if (nextSignature === summarySignature) return;
      summarySignature = nextSignature;
      summary.replaceChildren();
      summary.classList.add('nmvx-personalization__summary-list');

      for (const [label, value] of entries) {
        const chip = document.createElement('span');
        chip.className = 'nmvx-personalization__summary-chip';
        const chipLabel = document.createElement('small');
        const chipValue = document.createElement('span');
        chipLabel.textContent = label;
        chipValue.textContent = value;
        chip.append(chipLabel, chipValue);
        summary.appendChild(chip);
      }
    };

    const gateAcceleratedCheckout = (current) => {
      const productForm = form.closest('product-form-component');
      if (!productForm) return;

      const addButton = productForm.querySelector('[ref="addToCartButton"]');
      const variantAvailable = !(addButton instanceof HTMLButtonElement) || !addButton.disabled;
      const shouldHide = current.active && !current.complete;

      for (const checkout of productForm.querySelectorAll('[ref="acceleratedCheckoutButtonContainer"]')) {
        if (!(checkout instanceof HTMLElement)) continue;
        if (shouldHide) {
          if (!checkout.hidden) checkout.dataset.nmvxPersonalizationOwned = 'true';
          if (!checkout.hidden) checkout.hidden = true;
          continue;
        }
        if (checkout.dataset.nmvxPersonalizationOwned === 'true') {
          if (variantAvailable && checkout.hidden) checkout.hidden = false;
          delete checkout.dataset.nmvxPersonalizationOwned;
        }
      }
    };

    const controller = {
      card,
      context,
      variantPicker,
      reconcile() {
        const current = state();
        const genericMessage = card.dataset.requiredMessage
          || 'Complete the personalization before adding this item to your cart.';

        card.dataset.active = String(current.active);
        card.dataset.complete = String(current.complete);
        if (count) count.textContent = String(primary.value.length);
        if (phoneCount && phone instanceof HTMLInputElement) phoneCount.textContent = String(phone.value.length);

        primary.setCustomValidity('');
        phone?.setCustomValidity('');
        let message = '';
        let invalidTarget = primary;

        if (current.active && !current.complete) {
          if (mode === 'tag' && !current.primaryFilled) {
            message = 'Enter your dog’s name before adding this personalized tag to your cart.';
          } else if (mode === 'tag' && !current.phoneValid) {
            message = current.phoneFilled
              ? 'Enter a valid phone number containing 7 to 15 digits.'
              : 'Enter the phone number that should be encoded in the QR code.';
            invalidTarget = phone;
          } else {
            message = currentFileError || genericMessage;
          }
          invalidTarget?.setCustomValidity(message);
        }

        if (!current.active || current.complete) {
          if (currentFileError && attempted && !current.primaryFilled) showVisualError(currentFileError);
          else clearVisualError();
        } else if (attempted || currentFileError) {
          showVisualError(message || currentFileError || genericMessage, invalidTarget);
        } else {
          clearVisualError();
        }

        if (readiness) {
          readiness.textContent = current.complete ? 'Ready to order' : 'Personalization required';
          readiness.dataset.state = current.complete ? 'ready' : 'required';
        }

        if (current.file) {
          uploadLabel?.setAttribute('data-has-file', 'true');
          if (removeFile) removeFile.hidden = false;
          if (uploadTitle) uploadTitle.textContent = current.file.name;
          if (uploadMeta) uploadMeta.textContent = `${formatFileSize(current.file.size)} · Attached to this item`;
        } else if (mode === 'ammo') {
          uploadLabel?.removeAttribute('data-has-file');
          if (removeFile) removeFile.hidden = true;
          if (uploadTitle) uploadTitle.textContent = 'Upload your logo';
          if (uploadMeta) uploadMeta.textContent = 'PNG, JPG, JPEG, SVG, or PDF · Maximum 10 MB';
        }

        updateSummary(current);
        gateAcceleratedCheckout(current);
        return current;
      },
      schedule(includeFollowUp = false) {
        if (!frame) {
          frame = requestAnimationFrame(() => {
            frame = 0;
            try { controller.reconcile(); }
            catch (error) { console.error('[NAMVEXO personalization] Reconciliation failed safely.', error); }
          });
        }
        if (includeFollowUp && !secondFrame) {
          secondFrame = requestAnimationFrame(() => {
            secondFrame = requestAnimationFrame(() => {
              secondFrame = 0;
              try { controller.reconcile(); }
              catch (error) { console.error('[NAMVEXO personalization] Follow-up reconciliation failed safely.', error); }
            });
          });
        }
      },
      showRequiredError() {
        attempted = true;
        card.dataset.attempted = 'true';
        const current = controller.reconcile();
        if (current.complete) return;
        const target = mode === 'tag' && current.primaryFilled ? phone : primary;
        target?.reportValidity();
        card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        requestAnimationFrame(() => target?.focus({ preventScroll: true }));
      },
      prepareSubmission() {
        const current = controller.reconcile();
        if (!current.active || !current.complete) return;
        for (const input of temporarilyDisabled) input.disabled = false;
        temporarilyDisabled = [];

        if (mode === 'ammo') {
          if (!current.primaryFilled) {
            primary.disabled = true;
            temporarilyDisabled.push(primary);
          }
          if (!current.file && fileInput instanceof HTMLInputElement) {
            fileInput.disabled = true;
            temporarilyDisabled.push(fileInput);
          }
        }

        if (temporarilyDisabled.length) {
          queueMicrotask(() => {
            for (const input of temporarilyDisabled) input.disabled = false;
            temporarilyDisabled = [];
          });
        }
      },
      destroy() {
        if (frame) cancelAnimationFrame(frame);
        if (secondFrame) cancelAnimationFrame(secondFrame);
        abortController.abort();
        for (const input of temporarilyDisabled) input.disabled = false;
        temporarilyDisabled = [];
        controllers.delete(card);
        delete card.dataset.nmvxControllerReady;
      },
    };

    primary.addEventListener('input', () => {
      attempted = false;
      currentFileError = '';
      card.removeAttribute('data-attempted');
      controller.schedule();
    }, { signal });

    phone?.addEventListener('input', () => {
      attempted = false;
      card.removeAttribute('data-attempted');
      controller.schedule();
    }, { signal });

    fileInput?.addEventListener('change', () => {
      attempted = false;
      card.removeAttribute('data-attempted');
      currentFileError = logoError(fileInput.files?.[0] || null);
      if (currentFileError) {
        fileInput.value = '';
        attempted = true;
        card.dataset.attempted = 'true';
      }
      controller.schedule();
    }, { signal });

    removeFile?.addEventListener('click', () => {
      if (!(fileInput instanceof HTMLInputElement)) return;
      fileInput.value = '';
      currentFileError = '';
      attempted = false;
      card.removeAttribute('data-attempted');
      controller.reconcile();
      uploadLabel?.focus?.();
    }, { signal });

    context.addEventListener?.('change', () => controller.schedule(true), { signal });
    context.addEventListener?.('product:update', () => controller.schedule(true), { signal });
    context.addEventListener?.('variant:change', () => controller.schedule(true), { signal });

    controllers.set(card, controller);
    card.dataset.nmvxControllerReady = 'true';
    controller.reconcile();
  }

  function initialize(root = document) {
    const cards = root instanceof Element && root.matches(SELECTOR)
      ? [root]
      : root.querySelectorAll?.(SELECTOR) || [];
    for (const card of cards) {
      try { initCard(card); }
      catch (error) { console.error('[NAMVEXO personalization] Initialization failed safely.', error, card); }
    }
  }

  function cleanup(root) {
    if (!(root instanceof Element)) return;
    const cards = root.matches(SELECTOR) ? [root] : root.querySelectorAll(SELECTOR);
    for (const card of cards) controllers.get(card)?.destroy();
  }

  function installLifecycle() {
    if (lifecycleReady) return;
    lifecycleReady = true;
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));
  }

  function boot() {
    document.documentElement.classList.add('nmvx-ui-enabled', 'nmvx-ui-ready');
    installGlobalGuards();
    installLifecycle();
    initialize(document);
  }

  window[KEY] = { boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
